<template>
	<ModalV2
		v-model="open"
		:title="$t('personality_results.modal.heading')"
		size="xl"
		@close="open = false"
	>
		<template #default>
			<!-- Disclaimer -->
			<div class="px-6 py-4 mt-2 mb-6 rounded-lg bg-sky-100/80">
				<h4 class="mb-2 text-lg font-medium">
					{{ topSectionHeading }}
				</h4>
				<p class="mb-0 leading-snug">
					{{ topSectionBody }}
				</p>
			</div>

			<!-- Types -->
			<div v-for="(trait, index) in allTraitsDisplay" :key="index">
				<div class="flex space-x-3">
					<div>
						<TraitBadge :trait="trait.trait" />
					</div>
					<div>
						<!-- Trait name -->
						<h5
							class="mb-0 text-xl font-extrabold uppercase font-heading"
						>
							{{ trait.displayName }}
						</h5>

						<!-- Subtitle -->
						<h6
							class="mb-0 font-sans font-normal text-neutral-600 font-white"
						>
							{{ $t(trait.sloganKey) }}
						</h6>

						<!-- Description -->
						<p
							class="mt-3 mb-6 -ml-3 leading-normal text-neutral-700"
						>
							{{ $t(trait.descriptionKey) }}
						</p>

						<div
							class="grid grid-cols-1 -ml-3 gap-y-4 md:gap-y-0 md:grid-cols-2 md:gap-x-4"
						>
							<!-- Key strengths -->
							<div>
								<h6
									class="mb-3 font-sans font-medium text-neutral-900"
									:class="$lowercaseHeadingClass"
								>
									{{
										$t(
											`personality_results.view.${highlightHeadingView}.section.most_aligned`
										)
									}}
								</h6>
								<ul class="mb-0">
									<li
										v-for="(strength,
										index) in trait.mostAlignedKeys"
										:key="index"
										class="flex items-start my-1 space-x-2 text-neutral-600"
									>
										<Icon
											:icon="['far', 'check']"
											class="text-green-700 transform translate-y-[2px]"
										/>
										<div>
											{{ $t(strength) }}
										</div>
									</li>
								</ul>
							</div>

							<!-- Work style -->
							<div>
								<h6
									class="mb-3 font-sans font-medium text-neutral-900"
									:class="$lowercaseHeadingClass"
								>
									{{
										$t(
											`personality_results.view.${highlightHeadingView}.section.least_aligned`
										)
									}}
								</h6>
								<ul class="mb-0">
									<li
										v-for="(style,
										index) in trait.leastAlignedKeys"
										:key="index"
										class="flex my-1 text-neutral-600"
									>
										<div
											class="w-1 h-1 mr-2 rounded-full bg-neutral-600 flex-shrink-0 transform translate-y-[8px]"
										/>
										{{ $t(style) }}
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<hr v-if="index !== allTraitsDisplay.length - 1" class="my-6" />
			</div>
		</template>

		<template #footer="{ close }">
			<Button type="primary" class="w-full" @click="close()">
				{{ $t('general.close') }}
			</Button>
		</template>
	</ModalV2>
</template>

<script>
import personalityResults from '~/mixins/personalityResults'
import ModalV2 from '~/components/Modal/ModalV2'
import Button from '~/components/ui/common/Button'
import Icon from '~/components/ui/common/Icon'
import { TraitBadge } from '.'

export default {
	name: 'TraitsInfoModal',
	components: {
		ModalV2,
		Button,
		Icon,
		TraitBadge
	},
	mixins: [personalityResults],
	props: {
		value: {
			type: Boolean,
			required: true
		}
	},
	computed: {
		open: {
			get() {
				return this.value
			},
			set(value) {
				this.$emit('input', value)
			}
		},
		topSectionHeading() {
			return this.userIsApplicant
				? this.$t(
						'personality_results.modal.top_section.applicant.heading'
				  )
				: this.$t(
						'personality_results.modal.top_section.hiring_manager.heading'
				  )
		},
		topSectionBody() {
			return this.userIsApplicant
				? this.$t(
						'personality_results.modal.top_section.applicant.body'
				  )
				: this.$t(
						'personality_results.modal.top_section.hiring_manager.body'
				  )
		}
	}
}
</script>
